import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Droplets, Brush, AlertTriangle, Clock, CheckCircle2, XCircle, Download } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const HYGIENE_PROTOCOL_URL = "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/866e55ff8d7efb52cd119518c1e4581e82dc6ff764e6b18e1a19f1c8e92c07ea.jpeg";

const Section = ({ icon: Icon, title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-2xl font-bold text-neutral-900 flex items-center gap-2 mb-4">
      <Icon size={22} className="text-[#E8956B]" /> {title}
    </h2>
    <div className="text-neutral-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Card = ({ title, children }) => (
  <div className="bg-white border border-neutral-200 rounded-2xl p-5">
    <h3 className="font-bold text-neutral-900 mb-1.5">{title}</h3>
    <div className="text-sm text-neutral-600 leading-relaxed space-y-1.5">{children}</div>
  </div>
);

export default function CleanGuide() {
  usePageMeta(
    "How to Clean & Sanitize Used Makeup Safely | Beauty Trade Club",
    "Simple, trustworthy steps to sanitize and prep used makeup for resale — and how to shop pre-loved beauty safely. Lipsticks, powders, brushes, PAO & more.",
    "/clean-guide"
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="clean-guide-page">
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em] mb-4">
          <Sparkles size={15} /> Clean · Safe · Trusted
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 leading-tight">
          The Best Way to Clean &amp; Sanitize Your Makeup
        </h1>
        <p className="text-neutral-500 mt-4 max-w-2xl mx-auto">
          Whether you're getting items ready to sell or shopping for a pre-loved gem, a little know-how keeps
          everyone safe. Here's exactly how to sanitize makeup the right way — and what should never be resold.
        </p>
      </div>

      {/* Golden rules */}
      <div className="rounded-2xl bg-[#E8956B]/10 border border-[#E8956B]/30 p-6 mb-12">
        <h2 className="font-display text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
          <ShieldCheck size={20} className="text-[#E8956B]" /> The golden rules
        </h2>
        <ul className="space-y-2 text-sm text-neutral-700">
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#E8956B] shrink-0 mt-0.5" /> Use <strong>70% isopropyl (rubbing) alcohol</strong> — it sanitizes without leaving much residue. A small spray bottle is ideal.</li>
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#E8956B] shrink-0 mt-0.5" /> Always <strong>wash your hands</strong> and work on a clean surface with clean tools.</li>
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#E8956B] shrink-0 mt-0.5" /> <strong>Be honest</strong> about condition. Clear photos + accurate details build trust and better reviews.</li>
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#E8956B] shrink-0 mt-0.5" /> When in doubt, <strong>throw it out</strong>. Some products simply can't be safely resold (see below).</li>
        </ul>
      </div>

      {/* Downloadable hygiene protocol */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-6 mb-12 flex flex-col sm:flex-row items-center gap-6" data-testid="clean-hygiene-download">
        <img src={HYGIENE_PROTOCOL_URL} alt="Seller Hygiene Protocol" className="w-36 rounded-lg border border-neutral-200 shrink-0" />
        <div className="text-center sm:text-left">
          <h2 className="font-display text-xl font-bold text-neutral-900 mb-1.5 flex items-center justify-center sm:justify-start gap-2">
            <Download size={18} className="text-[#E8956B]" /> Grab the Seller Hygiene Protocol
          </h2>
          <p className="text-sm text-neutral-600 leading-relaxed mb-4">
            A printable one-pager with our clean-sanitize-pack checklist. Keep it handy while prepping items to sell —
            or just to know exactly how your pre-loved finds were cared for.
          </p>
          <a href={HYGIENE_PROTOCOL_URL} download="beauty-trade-club-hygiene-protocol.jpeg" target="_blank" rel="noreferrer"
            data-testid="clean-download-hygiene-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold text-sm px-5 py-2.5 transition-all">
            <Download size={16} /> Download hygiene protocol
          </a>
        </div>
      </div>

      {/* By product type */}
      <Section icon={Droplets} title="How to sanitize by product type">
        <div className="grid sm:grid-cols-2 gap-4">
          <Card title="💄 Lipstick & lip products">
            <p>Wipe off the very top layer with a clean tissue. Dip the bullet briefly in — or spritz with — 70% alcohol, then let it fully air-dry before capping.</p>
          </Card>
          <Card title="🎨 Powders (blush, eyeshadow, bronzer)">
            <p>Gently wipe or lightly scrape the top layer with a clean tissue. Mist the surface with a little alcohol and let it dry — it'll re-harden as it evaporates.</p>
          </Card>
          <Card title="🧴 Cream & liquid (foundation, concealer)">
            <p>For jars/pans, scrape off the top layer. Wipe the packaging, pump and nozzle with alcohol. Note: product inside a used pot is hardest to sanitize — sell only if lightly used and be transparent.</p>
          </Card>
          <Card title="✏️ Pencils (eye & lip liner)">
            <p>Sharpen until you reveal a completely fresh tip, then wipe the exposed tip with alcohol. Sharpening removes the used surface entirely.</p>
          </Card>
          <Card title="🖌️ Brushes & tools">
            <p>Wash with gentle brush cleaner or mild soap and lukewarm water, rinse until clear, reshape the bristles, and dry flat. Sponges are best sold new/unused only.</p>
          </Card>
          <Card title="💅 Compacts, palettes & cases">
            <p>Wipe mirrors, hinges and casing with an alcohol wipe. Sanitize each pan surface as you would loose powders or creams above.</p>
          </Card>
        </div>
      </Section>

      {/* Do not resell */}
      <Section icon={AlertTriangle} title="What you should NOT resell">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <ul className="space-y-2 text-sm text-red-800">
            <li className="flex gap-2"><XCircle size={16} className="shrink-0 mt-0.5" /> <strong>Mascara & liquid eyeliner</strong> — bacteria grow fast in the tube and they can't be sanitized. Sell only if brand-new & sealed.</li>
            <li className="flex gap-2"><XCircle size={16} className="shrink-0 mt-0.5" /> Anything <strong>past its expiry or PAO</strong> date (see below).</li>
            <li className="flex gap-2"><XCircle size={16} className="shrink-0 mt-0.5" /> Products with an <strong>off smell, changed texture, or visible mold</strong>.</li>
            <li className="flex gap-2"><XCircle size={16} className="shrink-0 mt-0.5" /> Items used while you had an <strong>eye or lip infection</strong> (cold sore, conjunctivitis, stye).</li>
          </ul>
        </div>
      </Section>

      {/* PAO / expiry */}
      <Section icon={Clock} title="Check the PAO (Period After Opening)">
        <p>
          Most beauty products show a small <strong>open-jar symbol</strong> with a number like <em>12M</em> or <em>6M</em> —
          that's how many months the product stays good <strong>after it's first opened</strong>. As a rough guide:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Mascara & liquid liner: <strong>3 months</strong></li>
          <li>Liquid foundation & concealer: <strong>6–12 months</strong></li>
          <li>Cream products: <strong>6–12 months</strong></li>
          <li>Powders (blush, eyeshadow, bronzer): <strong>up to 2 years</strong></li>
          <li>Lipstick & lip gloss: <strong>1–2 years</strong></li>
        </ul>
        <p className="text-sm">When you list an item, add the batch code or PAO in your description so buyers know it's fresh.</p>
      </Section>

      {/* Buyer reassurance */}
      <Section icon={ShieldCheck} title="Shopping pre-loved? You're protected">
        <p>
          Every seller is verified, and every order is <strong>escrow-protected</strong> — the seller is only paid after
          you confirm your item arrived as described. When your order lands, give it a quick check, and do a light
          sanitizing pass of your own before first use, just like the pros.
        </p>
      </Section>

      {/* CTA */}
      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        <Link to="/sell" data-testid="clean-cta-sell" className="rounded-2xl bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold text-center py-4 transition-all">
          Prep &amp; list an item →
        </Link>
        <Link to="/shop" data-testid="clean-cta-shop" className="rounded-2xl border-2 border-[#E8956B] text-[#CE744A] hover:bg-[#E8956B]/10 font-bold text-center py-4 transition-all">
          Shop pre-loved beauty →
        </Link>
      </div>
      <p className="text-center text-xs text-neutral-400 mt-6">
        This guide is general best-practice advice, not medical guidance. If you have sensitive skin or allergies, patch-test first.
      </p>
    </div>
  );
}
